import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cartQueryKeys, useCart } from "@/hooks/useCart";
import { sdk } from "@/lib/medusa/client";
import { StoreCart } from "@medusajs/types";
import { useDebouncedCallback } from "use-debounce";

type WishlistMetada = {
  wishlist_ids?: string[];
};
export const useAddToCart = (options?: {
  onSuccess?: (updatedCart: StoreCart) => void;
  onError?: (error: Error) => void;
}) => {
  const { cart, setCart } = useCart();

  const mutation = useMutation({
    mutationFn: async ({
      variantId,
      quantity,
      metadata,
    }: {
      variantId: string;
      quantity: number;
      metadata?: WishlistMetada;
    }) => {
      if (!cart) {
        throw new Error("No cart available");
      }

      const { cart: updatedCart } = await sdk.store.cart.createLineItem(cart.id, {
        variant_id: variantId,
        quantity,
        metadata,
      });

      return updatedCart;
    },
    onSuccess: (updatedCart) => {
      setCart(updatedCart);
      options?.onSuccess?.(updatedCart);
    },
    onError: (error: Error) => {
      console.error("Error adding item to cart:", error);
      options?.onError?.(error);
    },
  });

  return {
    addToCart: (variantId: string, quantity: number, metadata?: WishlistMetada) =>
      mutation.mutate({ variantId, quantity, metadata }),
    addToCartAsync: (variantId: string, quantity: number, metadata?: WishlistMetada) =>
      mutation.mutateAsync({ variantId, quantity, metadata }),
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};

// Hook for updating item quantities
export const useUpdateCartItem = (options?: {
  onSuccess?: (updatedCart: StoreCart) => void;
  onError?: (error: Error) => void;
}) => {
  const { cart, setCart } = useCart();

  const mutation = useMutation({
    mutationFn: async ({
      lineItemId,
      quantity,
      metadata,
    }: {
      lineItemId: string;
      quantity: number;
      metadata?: WishlistMetada;
    }) => {
      if (!cart) {
        throw new Error("No cart available");
      }

      const { cart: updatedCart } = await sdk.store.cart.updateLineItem(cart.id, lineItemId, { quantity, metadata });

      return updatedCart;
    },
    onSuccess: (updatedCart) => {
      setCart(updatedCart);
      options?.onSuccess?.(updatedCart);
    },
    onError: (error: Error) => {
      console.error("Error updating cart item:", error);
      options?.onError?.(error);
    },
  });

  const debouncedUpdate = useDebouncedCallback(
    (lineItemId: string, quantity: number, metadata?: WishlistMetada) => {
      mutation.mutate({ lineItemId, quantity, metadata });
    },
    300 // debounce delay in ms
  );

  return {
    updateQuantity: debouncedUpdate,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};

export const useRemoveCartItem = (options?: {
  onSuccess?: (updatedCart?: StoreCart) => void;
  onError?: (error: Error) => void;
}) => {
  const { cart } = useCart();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (lineItemId: string) => {
      if (!cart) {
        throw new Error("No cart available");
      }
      const { parent: updatedCart } = await sdk.store.cart.deleteLineItem(cart.id, lineItemId);
      return updatedCart;
    },
    onMutate: async (lineItemId) => {
      if (!cart) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: cartQueryKeys.cart(cart.id) });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData(cartQueryKeys.cart(cart.id));

      // Optimistically update the cache
      const optimisticCart = {
        ...cart,
        items: cart.items?.filter((item) => item.id !== lineItemId) || [],
      };

      // Update the query cache ( will update useCart context )
      queryClient.setQueryData(cartQueryKeys.cart(cart.id), optimisticCart);

      return { previousCart };
    },
    onError: (err, _, context) => {
      if (context?.previousCart && cart) {
        queryClient.setQueryData(cartQueryKeys.cart(cart.id), context.previousCart);
      }
      options?.onError?.(err);
    },
    onSuccess: (updatedCart) => {
      if (cart && updatedCart) {
        // Only update if the server response is different from  optimistic update
        queryClient.setQueryData(cartQueryKeys.cart(cart.id), updatedCart);
      }
      options?.onSuccess?.(updatedCart);
    },
  });

  return {
    removeItem: (lineItemId: string) => mutation.mutate(lineItemId),
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};

// Hook for clearing the entire cart
export const useClearCart = (options?: {
  onSuccess?: (updatedCart: StoreCart) => void;
  onError?: (error: Error) => void;
}) => {
  const { cart, setCart } = useCart();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!cart || !cart.items?.length) {
        throw new Error("No cart available or cart is already empty");
      }

      // Delete all items
      await Promise.all(cart.items.map((item) => sdk.store.cart.deleteLineItem(cart.id, item.id)));

      // Refresh cart to get updated state
      const { cart: updatedCart } = await sdk.store.cart.retrieve(cart.id);
      return updatedCart;
    },
    onSuccess: (updatedCart) => {
      setCart(updatedCart);
      options?.onSuccess?.(updatedCart);
    },
    onError: (error: Error) => {
      console.error("Error clearing cart:", error);
      options?.onError?.(error);
    },
  });

  return {
    clearCart: () => mutation.mutate(),
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
